import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';

export default function OptoutUserModal({
  open,
  onClose,
  initialPhoneNumber = '',
  onConfirm,
  isSubmitting = false,
}) {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (open) {
      setPhoneNumber(initialPhoneNumber ? String(initialPhoneNumber).trim() : '');
      setValidationError('');
    }
  }, [open, initialPhoneNumber]);

  const cleanNumber = (val) =>
    String(val ?? '')
      .trim()
      .replace(/^\+/, '')
      .replace(/\s+/g, '');

  const handleChange = (e) => {
    const val = e.target.value;
    setPhoneNumber(val);
    if (validationError) {
      setValidationError('');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const cleaned = cleanNumber(phoneNumber);

    if (!cleaned) {
      setValidationError('Phone number is required');
      return;
    }

    if (!/^\d{7,15}$/.test(cleaned)) {
      setValidationError(
        'Please enter a valid phone number with country code (digits only, e.g. 919068544561)'
      );
      return;
    }

    try {
      await onConfirm(cleaned);
    } catch {
      // Error handled by parent/snackbar
    }
  };

  const isPrefilled = Boolean(initialPhoneNumber);

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 1,
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonRemoveIcon color="error" />
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            Opt Out User
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          {isPrefilled ? (
            <Box sx={{ mb: 2 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Are you sure you want to opt out user{' '}
                <Typography component="span" sx={{ fontWeight: 700 }}>
                  {cleanNumber(phoneNumber) || initialPhoneNumber}
                </Typography>
                ?
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Once opted out, this user will no longer receive any WhatsApp template messages.
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter the user&apos;s phone number to opt them out from receiving WhatsApp template
              messages.
            </Typography>
          )}

          <TextField
            autoFocus={!isPrefilled}
            fullWidth
            size="small"
            label="Phone Number"
            placeholder="e.g. 919068544561"
            value={phoneNumber}
            onChange={handleChange}
            error={Boolean(validationError)}
            helperText={validationError || 'Include country code (e.g. 919068544561)'}
            disabled={isSubmitting}
            InputProps={{
              sx: { fontFamily: 'monospace' },
            }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isSubmitting}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="error"
            disabled={isSubmitting || !cleanNumber(phoneNumber)}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <PersonRemoveIcon fontSize="small" />
              )
            }
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {isSubmitting ? 'Opting out…' : 'Opt Out User'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
